import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";

const API_URL = "https://zpanel.zentragroup.cl/app/api";

const formatCLP = (n) =>
  new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(Number(n || 0));

const formatDate = (iso) => {
  if (!iso) return "-";

  const fecha = String(iso).substring(0, 10);
  const [year, month, day] = fecha.split("-");

  if (!year || !month || !day) return "-";

  return `${day}-${month}-${year}`;
};

export default function MiPerfil() {
  const navigate = useNavigate();
  const [client, setClient] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem("cliente");

    if (!stored) {
      navigate("/Cliente");
      return;
    }

    const clienteBD = JSON.parse(stored);

    fetch(`${API_URL}/cliente_full.php?cliente_id=${clienteBD.id}`)
      .then((res) => res.json())
      .then((data) => {
        if (!data.success) {
          navigate("/Cliente");
          return;
        }

        setClient({
          nombre: `${data.cliente.nombre || ""} ${data.cliente.apellido || ""}`.trim(),
          rut: data.cliente.rut || "-",
          email: data.cliente.correo || "-",
          telefono: data.cliente.telefono || "-",
          direccion: data.cliente.direccion || "-",

          servicios: (data.servicios || []).map((s) => {
            const precio = Number(s.valor_mensual || 0);
            const pagado = Number(s.monto_pagado || 0);
            const plan = s.tipo_servicio || "-";

            const saldo =
              plan === "UNICO"
                ? Math.max(precio - pagado, 0)
                : precio;

            return {
              id: s.id,
              nombre: s.nombre_servicio || `Servicio #${s.servicio_id}`,
              plan,
              precio,
              pagado,
              saldo,
              expira: s.fecha_expiracion,
              estado: Number(s.estado) === 1 ? "Activo" : "Pagado",
            };
          }),

          comprobantes: (data.pagos || []).map((p) => ({
            id: p.id,
            tipo: p.tipo_pago || "Pago",
            folio: p.folio_boleta || "-",
            fecha: p.fecha_pago,
            monto: p.monto || 0,
            enlace: p.archivo_boleta_url || "#",
          })),
        });
      })
      .catch(() => {
        navigate("/Cliente");
      });
  }, [navigate]);

  if (!client) return null;

  const serviciosActivos = client.servicios.filter(
    (s) => s.estado === "Activo"
  );

  const totalMensual = serviciosActivos.reduce(
    (acc, s) => acc + Number(s.saldo || 0),
    0
  );

  return (
    <div className="min-h-screen bg-zentra-bg">
      <Navbar />

      <main className="mx-auto max-w-5xl px-4 pt-16 pb-20">
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">
          Mi perfil
        </h1>
        <p className="mt-1 text-sm text-slate-700">
          Revisa tus datos, tus servicios contratados y fechas de expiración.
        </p>

        <section className="mt-6 relative overflow-hidden rounded-3xl bg-white/95 text-slate-900 ring-1 ring-slate-200 shadow-[0_24px_70px_rgba(15,23,42,0.4)]">
          <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-[radial-gradient(800px_260px_at_0%_0%,rgba(32,75,151,0.25),transparent_60%),radial-gradient(800px_260px_at_100%_0%,rgba(54,192,185,0.25),transparent_60%)]" />

          <div className="relative p-6 md:p-8">
            <div className="grid gap-6 md:grid-cols-2">
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
                  Datos del titular
                </p>
                <h2 className="mt-2 text-2xl md:text-3xl font-semibold text-slate-900">
                  {client.nombre}
                </h2>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center rounded-full bg-slate-900 text-slate-50 px-3 py-1 text-xs font-mono">
                    RUT&nbsp;{client.rut}
                  </span>
                  <span className="inline-flex items-center rounded-full border border-slate-200 bg-white/80 px-3 py-1 text-xs text-slate-600">
                    Cliente Zentra
                  </span>
                </div>

                <dl className="mt-4 space-y-1 text-sm text-slate-700">
                  <div className="flex gap-2">
                    <dt className="w-20 text-slate-500">Correo</dt>
                    <dd>{client.email}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="w-20 text-slate-500">Teléfono</dt>
                    <dd>{client.telefono}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="w-20 text-slate-500">Dirección</dt>
                    <dd>{client.direccion}</dd>
                  </div>
                </dl>
              </div>

              <div className="md:border-l md:border-slate-200 md:pl-6">
                <p className="text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
                  Resumen de servicios
                </p>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="text-3xl font-semibold text-slate-900">
                    {formatCLP(totalMensual)}
                  </span>
                  <span className="text-sm text-slate-500"></span>
                </div>
                <p className="mt-1 text-sm text-slate-600">
                  Monto total de tus servicios activos.
                </p>

                <div className="mt-4 space-y-2 text-sm">
                  {serviciosActivos.length === 0 ? (
                    <div className="rounded-xl bg-slate-50 px-3 py-2 border border-slate-200 text-slate-500">
                      Sin servicios activos.
                    </div>
                  ) : (
                    serviciosActivos.map((s) => (
                      <div
                        key={s.id}
                        className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2 border border-slate-200"
                      >
                        <div>
                          <p className="font-medium text-slate-900">
                            {s.nombre}{" "}
                            <span className="text-xs text-slate-500">
                              · {s.plan}
                            </span>
                          </p>
                          <p className="text-xs text-slate-500">
                            Vence el {formatDate(s.expira)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-slate-900">
                            {formatCLP(s.saldo)}
                          </p>
                          <span
                            className={`mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
                              s.estado === "Activo"
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                : "bg-amber-50 text-amber-700 border border-amber-200"
                            }`}
                          >
                            {s.estado || "Sin estado"}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="mt-7 border-t border-slate-200 pt-5">
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-slate-500 mb-3">
                Detalle de servicios
              </p>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500">
                      <th className="py-2 pr-4">Servicio</th>
                      <th className="py-2 pr-4">Plan</th>
                      <th className="py-2 pr-4">Valor</th>
                      <th className="py-2 pr-4">Abonado</th>
                      <th className="py-2 pr-4">Saldo</th>
                      <th className="py-2 pr-4">Fecha de expiración</th>
                      <th className="py-2">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {client.servicios.map((s) => (
                      <tr
                        key={s.id}
                        className="border-t border-slate-100 last:border-b"
                      >
                        <td className="py-2 pr-4 text-slate-900">{s.nombre}</td>
                        <td className="py-2 pr-4 text-slate-700">{s.plan}</td>
                        <td className="py-2 pr-4 text-slate-900">
                          {formatCLP(s.precio)}
                        </td>
                        <td className="py-2 pr-4 text-slate-900">
                          {formatCLP(s.pagado)}
                        </td>
                        <td className="py-2 pr-4 text-slate-900">
                          {formatCLP(s.saldo)}
                        </td>
                        <td className="py-2 pr-4 text-slate-700">
                          {formatDate(s.expira)}
                        </td>
                        <td className="py-2">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${
                              s.estado === "Activo"
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                : "bg-amber-50 text-amber-700 border border-amber-200"
                            }`}
                          >
                            {s.estado || "Sin estado"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-7 border-t border-slate-200 pt-5">
              <div className="flex items-center justify-between gap-2 mb-3">
                <p className="text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
                  Boletas y facturas
                </p>
                <span className="text-xs text-slate-500">
                  {client.comprobantes.length}{" "}
                  {client.comprobantes.length === 1
                    ? "comprobante"
                    : "comprobantes"}
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500">
                      <th className="py-2 pr-4">Tipo</th>
                      <th className="py-2 pr-4">Folio</th>
                      <th className="py-2 pr-4">Fecha</th>
                      <th className="py-2 pr-4">Monto</th>
                      <th className="py-2">Documento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {client.comprobantes.map((c) => (
                      <tr
                        key={c.id}
                        className="border-t border-slate-100 last:border-b"
                      >
                        <td className="py-2 pr-4 text-slate-900">{c.tipo}</td>
                        <td className="py-2 pr-4 text-slate-700">{c.folio}</td>
                        <td className="py-2 pr-4 text-slate-700">
                          {formatDate(c.fecha)}
                        </td>
                        <td className="py-2 pr-4 text-slate-900">
                          {formatCLP(c.monto)}
                        </td>
                        <td className="py-2">
                          {c.enlace && c.enlace !== "#" ? (
                            <a
                              href={c.enlace}
                              download
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-700 hover:border-[var(--brand-1)] hover:text-[var(--brand-1)]"
                            >
                              Descargar PDF
                            </a>
                          ) : (
                            <span className="text-xs text-slate-400">Sin archivo</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="mt-2 text-xs text-slate-500">
                Si necesitas una copia de una boleta o factura específica,
                también puedes solicitarla a soporte@zentragroup.cl.
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}